import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import API from '@/lib/api';
import { scanArticle, scanDocumentLine } from '@/lib/scanQuantity';
import { makeScanItem } from '@/lib/scanItems';
import { cashError } from '@/lib/cashReceipts';

export const CashAddArticle = ({ onAdd, disabled, onBusyChange }) => {
  const [query, setQuery] = useState(''), [results, setResults] = useState([]), [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const selection = useRef(null);
  const sequence = useRef(0);
  useEffect(() => () => { sequence.current += 1; selection.current?.abort(); }, []);
  useEffect(() => { onBusyChange?.(busy || !!query.trim() || !!choice); }, [busy, query, choice, onBusyChange]);
  useEffect(() => {
    setResults([]);
    if (query.trim().length < 2 || disabled) { setBusy(false); return; }
    let current = true;
    const controller = new AbortController();
    setBusy(true);
    const timer = setTimeout(async () => {
      try { const r = await API.get('/artikels-zoek', { params: { q: query, limit: 8 }, signal: controller.signal }); if (current) setResults(r.data); }
      catch (e) { if (current) setError(cashError(e)); }
      finally { if (current) setBusy(false); }
    }, 250);
    return () => { current = false; clearTimeout(timer); controller.abort(); };
  }, [query, disabled]);
  const append = (art, variant) => { onAdd({ ...scanDocumentLine(makeScanItem(art, variant)), regel_id: crypto.randomUUID() }); setChoice(null); setResults([]); setQuery(''); };
  const select = async result => {
    const selectedSequence = ++sequence.current;
    selection.current?.abort();
    const controller = new AbortController();
    selection.current = controller;
    setError(''); setBusy(true);
    try {
      const art = scanArticle((await API.get(`/artikels/${result.art_id || result.id}`, { signal: controller.signal })).data);
      if (selectedSequence !== sequence.current) return;
      const variants = (art.varianten || []).filter(v => v.actief !== false);
      const selected = result.is_variant && variants.find(v => v.combo_key === (result.variant_combo_key || result.combo_key));
      if (selected) append(art, selected);
      else if (variants.length) { setChoice({ art, variants }); setQuery(''); }
      else append(art, null);
    } catch (e) { if (selectedSequence === sequence.current) setError(cashError(e)); }
    finally { if (selectedSequence === sequence.current) setBusy(false); }
  };
  return <div className="mt-4">
    <label className="text-xs text-gray-500 flex gap-2 items-center mb-2" htmlFor="cash-add-query"><MagnifyingGlass size={15} /> Artikel toevoegen</label>
    <input id="cash-add-query" data-testid="cash-add-query" className="cash-date" placeholder="Artikelcode, barcode of omschrijving" value={query} disabled={disabled} onChange={e => { sequence.current += 1; selection.current?.abort(); setQuery(e.target.value); setError(''); setChoice(null); }} />
    {busy && <span role="status" data-testid="cash-add-loading" className="text-xs text-gray-500">Zoeken…</span>}
    {error && <p role="alert" data-testid="cash-add-error" className="text-sm text-red-700">{error}</p>}
    {!busy && !error && query.trim().length >= 2 && !results.length && <p className="text-xs text-gray-500 mt-2" data-testid="cash-add-empty">Geen artikelen gevonden.</p>}
    {!!results.length && <div className="border border-gray-200 max-h-48 overflow-auto" data-testid="cash-add-results">{results.map((r, i) => <button type="button" key={`${r.id}-${i}`} className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50" disabled={disabled || busy} data-testid={`cash-add-option-${i}`} onClick={() => select(r)}>{r.art_code} · {r.omschrijving}</button>)}</div>}
    {choice && <select className="cash-date mt-2" data-testid="cash-add-variant" value="" disabled={disabled} aria-label="Artikelvariant" onChange={e => append(choice.art, choice.variants[Number(e.target.value)])}>
      <option value="" disabled>Kies variant van {choice.art.art_code}</option>
      {choice.variants.map((v, i) => <option key={i} value={i}>{v.art_code || v.code} · {Object.values(v.combo || {}).join(' · ')}</option>)}
    </select>}
  </div>;
};